/**
 * Voice Command Detection Service
 * Detects and extracts voice commands from transcribed text
 */

export type VoiceCommandType =
  | 'end_conversation'
  | 'translate_word'
  | 'repeat_sentence'
  | 'translate_sentence'
  | 'create_flashcard'
  | 'set_speed'
  | 'none'

export interface VoiceCommand {
  type: VoiceCommandType
  parameter?: string // For commands with parameters (word to translate, speed value, etc.)
  originalText: string
}

/**
 * Detect voice commands in transcribed text
 * Returns the command type and any extracted parameters
 *
 * Strategy: All commands must start with a wake word to distinguish them
 * from regular conversation in the target language.
 *
 * Examples:
 * - "toko repeat" - repeats last sentence
 * - "hey toko slow down" - slows playback
 * - "toko what did you say" - translates last sentence
 * - "toko end conversation" - ends the session
 */
export function detectVoiceCommand(text: string): VoiceCommand {
  const normalized = text.toLowerCase().trim()

  // Debug logging
  console.log('[VoiceCommand] Analyzing text:', {
    original: text,
    normalized: normalized,
  })

  // 0. Standalone Commands (No wake word required)
  // These must be exact or very close matches to avoid false positives during practice
  const standaloneCommands: Array<{ pattern: RegExp, type: VoiceCommandType, parameter?: string }> = [
    // End Conversation / Stop
    { pattern: /^stop\.?$/i, type: 'end_conversation' },
    { pattern: /^end\s+conversation\.?$/i, type: 'end_conversation' },
    { pattern: /^finish\s+conversation\.?$/i, type: 'end_conversation' },
    { pattern: /^stop\s+it\.?$/i, type: 'end_conversation' },
    { pattern: /^i\s+want\s+to\s+stop\.?$/i, type: 'end_conversation' },

    // Repeat
    { pattern: /^repeat\.?$/i, type: 'repeat_sentence' },
    { pattern: /^say\s+that\s+again\.?$/i, type: 'repeat_sentence' },
    { pattern: /^one\s+more\s+time\.?$/i, type: 'repeat_sentence' },
    { pattern: /^what\s+did\s+you\s+say\??$/i, type: 'translate_sentence' }, // Ambiguous, but usually a request for help

    // Translation
    { pattern: /^translate\s+that\.?$/i, type: 'translate_sentence' },
    { pattern: /^english\s+please\.?$/i, type: 'translate_sentence' },

    // Speed
    { pattern: /^slow\s+down\.?$/i, type: 'set_speed', parameter: 'slower' },
    { pattern: /^speak\s+slower\.?$/i, type: 'set_speed', parameter: 'slower' },
  ]

  for (const cmd of standaloneCommands) {
    if (cmd.pattern.test(normalized)) {
      console.log('[VoiceCommand] Detected standalone command:', cmd.type)
      return {
        type: cmd.type,
        parameter: cmd.parameter,
        originalText: text,
      }
    }
  }

  // Check for wake word at the start
  // Allow variations: "toko", "tokyo", "taco" (common STT errors)
  // Added: "hey toko", "ok toko", "coco", "to go", "doc", "doko", "toe", "two", "took", "tock"
  const wakeWordPattern = /^(?:hey\s+)?(?:ok\s+)?(toko|tokyo|taco|talk|coco|cocoa|to\s+go|doc|doko|toe|two|took|tock|top|total)\s+(.+)/i
  const wakeWordMatch = normalized.match(wakeWordPattern)

  if (!wakeWordMatch) {
    // No wake word detected - not a command
    return {
      type: 'none',
      originalText: text,
    }
  }

  // Extract the command after the wake word (already normalized to lowercase)
  // Strip trailing punctuation which STT often adds
  const commandText = wakeWordMatch[2].trim().replace(/[.,!?]+$/, '')

  // Map common STT transcription errors for Spanish words
  const sttErrorMap: Record<string, string> = {
    'k': 'qué',  // Use accented version which AI might recognize better
    'kay': 'qué',
    'cue': 'qué',
    'q': 'qué',
  }

  // Helper to correct word transcription errors
  const correctWord = (word: string): string => {
    return sttErrorMap[word.toLowerCase()] || word
  }

  // Helper: Check for word boundary matches (strict English command detection)
  const hasCommand = (patterns: RegExp[]): boolean => {
    return patterns.some(pattern => pattern.test(commandText))
  }

  // Helper: Check for exact phrase matches (case-insensitive)
  const hasPhrase = (phrases: string[]): boolean => {
    return phrases.some(phrase => commandText.includes(phrase))
  }

  // 1. End conversation
  // Patterns: "toko end conversation", "toko stop", "toko finish", "toko bye"
  const endConversationStrict = [
    /^end\s+(?:the\s+)?conversation$/,
    /^finish\s+(?:the\s+)?conversation$/,
    /^stop\s+(?:the\s+)?conversation$/,
    /^quit$/,
    /^stop$/,
    /^end$/,
    /^finish$/,
    /^bye$/,
    /^goodbye$/,
    /^exit$/,
    /^leave$/,
  ]

  // Fallback: Common STT transcription errors
  const endConversationFuzzy = [
    'and conversation',
    'and conversación',
    'end conversación',
  ]

  if (hasCommand(endConversationStrict) || hasPhrase(endConversationFuzzy)) {
    return {
      type: 'end_conversation',
      originalText: text,
    }
  }

  // 2. Translate sentence
  // Priority: Check sentence translation first to avoid conflicts
  const translateSentenceStrict = [
    /\btranslate\s+(?:the|that\s+)?sentence\b/,
    /\bwhat\s+did\s+you\s+say\b/,
    /\bwhat\s+does\s+that\s+mean\b/,
    /\bwhat\s+was\s+that\b/,
    /\btranslate\s+(?:the|that)\s+(?:whole|entire)\b/,
    /\bsay\s+(?:that|it)\s+in\s+english\b/,
    /\benglish\s+please\b/,
    /^meaning\??$/,
  ]

  if (hasCommand(translateSentenceStrict)) {
    return {
      type: 'translate_sentence',
      originalText: text,
    }
  }

  // 3. Translate word - "translate [word]"
  // Check this BEFORE repeat to avoid "translate que" triggering repeat

  // Special case: Just "translate" alone means translate the last sentence
  if (commandText === 'translate') {
    return {
      type: 'translate_sentence',
      originalText: text,
    }
  }

  const translateWordPatterns = [
    /\btranslate\s+(.+)/,  // More permissive: captures everything after "translate"
    /\bwhat\s+does\s+(\S+)\s+mean/i,  // "what does X mean"
    /\bput\s+(?:this|the)\s+(\S+)\s+mean/i,  // STT variation: "put this X mean" → "what does X mean"
    /\bwhat\s+is\s+(\S+)/i,  // "what is X"
    /\bwhat's\s+(\S+)/i,  // "what's X"
    /\bmeaning\s+of\s+(\S+)/i,  // "meaning of X"
    /\bdefine\s+(\S+)/i, // "define X"
    /\bhow\s+do\s+you\s+say\s+(.+?)(?:\s+in\s+\w+)?$/,
  ]

  // Make sure it's not asking to translate a sentence
  // Allow specific patterns like "put this X mean" even though they contain "the"
  const allowedPhrases = /\bput\s+(?:this|the)\s+\S+\s+mean/i
  const isValidWordContext = !commandText.includes('sentence') &&
    !commandText.includes('that') &&
    (commandText.includes('the ') ? allowedPhrases.test(commandText) : true)

  if (isValidWordContext) {
    for (const pattern of translateWordPatterns) {
      const match = commandText.match(pattern)
      if (match && match[1] && match[1].trim()) {
        let word = match[1].trim()
        // Additional validation: make sure it's not too long (likely a sentence)
        const wordCount = word.split(/\s+/).length
        if (wordCount <= 3) {  // Allow up to 3 words for phrases like "por favor"
          // Correct common STT transcription errors
          const originalWord = word
          word = correctWord(word)

          if (originalWord !== word) {
            console.log('[VoiceCommand] Corrected word:', { original: originalWord, corrected: word })
          }

          return {
            type: 'translate_word',
            parameter: word,
            originalText: text,
          }
        }
      }
    }
  }

  // 4. Repeat sentence
  // Priority: Strict English commands (more specific patterns)
  const repeatStrict = [
    /\brepeat\b/,
    /\bsay\s+(?:that|it)\s+again\b/,
    /\bone\s+more\s+time\b/,
    /\bplay\s+(?:that|it)\s+again\b/,
    /\bpardon\b/,
    /^what\??$/, // Just "what?"
    /^again$/,   // Just "again" (careful with false positives, but with wake word it's safer)
  ]

  // Fallback: Only allow Spanish if it's a clear command phrase
  const repeatFuzzy = [
    'repite',
    'repetir',
  ]

  if (hasCommand(repeatStrict) || hasPhrase(repeatFuzzy)) {
    return {
      type: 'repeat_sentence',
      originalText: text,
    }
  }

  // 5. Create flashcard - "make a flashcard of [word]"
  const flashcardPatterns = [
    /(?:make|create)\s+(?:a\s+)?flashcard\s+(?:of|for|with)\s+(.+)/,
    /(?:add|save)\s+(.+?)\s+(?:to|as\s+a)\s+flashcard/,
    /flashcard\s+(.+)/,
    /remember\s+(.+)/,
    /study\s+(.+)/,
  ]

  for (const pattern of flashcardPatterns) {
    const match = commandText.match(pattern)
    if (match && match[1]) {
      return {
        type: 'create_flashcard',
        parameter: match[1].trim(),
        originalText: text,
      }
    }
  }

  // 6. Playback Speed
  const speedPatterns = [
    { pattern: /slow\s+down/, value: 'slower' },
    { pattern: /speak\s+slower/, value: 'slower' },
    { pattern: /too\s+fast/, value: 'slower' },
    { pattern: /normal\s+speed/, value: 'normal' },
    { pattern: /speak\s+normal/, value: 'normal' },
    { pattern: /faster/, value: 'faster' }, // Less common but good to have
  ]

  for (const { pattern, value } of speedPatterns) {
    if (pattern.test(commandText)) {
      return {
        type: 'set_speed',
        parameter: value,
        originalText: text,
      }
    }
  }

  // No command detected
  return {
    type: 'none',
    originalText: text,
  }
}

/**
 * Check if text contains a voice command
 */
export function hasVoiceCommand(text: string): boolean {
  return detectVoiceCommand(text).type !== 'none'
}

/**
 * Get user-friendly description of command
 */
export function getCommandDescription(command: VoiceCommand): string {
  switch (command.type) {
    case 'end_conversation':
      return 'Ending conversation...'
    case 'translate_word':
      return `Translating "${command.parameter}"...`
    case 'repeat_sentence':
      return 'Repeating last sentence...'
    case 'translate_sentence':
      return 'Translating last sentence...'
    case 'create_flashcard':
      return `Creating flashcard for "${command.parameter}"...`
    case 'set_speed':
      return `Setting speed to ${command.parameter}...`
    default:
      return ''
  }
}
